import { useState, useRef, useCallback } from "react";
import { toast } from "react-hot-toast";

export function usePiP() {
  const [pipActive, setPipActive] = useState(false);
  const pipWindowRef = useRef<Window | null>(null);

  const updatePip = useCallback((orig: string, tran: string) => {
    if (!pipWindowRef.current) return;
    const doc = pipWindowRef.current.document;
    const w = doc.getElementById("pip-wait");
    const o = doc.getElementById("pip-orig");
    const t = doc.getElementById("pip-tran");
    if (w) w.style.display = "none";
    if (o) o.innerText = orig;
    if (t) t.innerText = tran;
  }, []);

  const closePip = useCallback(() => {
    if (pipWindowRef.current) {
      pipWindowRef.current.close();
      pipWindowRef.current = null;
    }
    setPipActive(false);
  }, []);

  const openPip = useCallback(async () => {
    if (!("documentPictureInPicture" in window)) {
      toast(
        "Tu navegador actual no admite la ventana flotante (Document PiP). Para usar esta función, te recomendamos abrir Kira desde Google Chrome o Microsoft Edge en PC o Mac.",
        { icon: "ℹ️", duration: 5500 }
      );
      return;
    }
    try {
      const pipWindow = await (window as any).documentPictureInPicture.requestWindow({ width: 560, height: 180 });
      pipWindowRef.current = pipWindow;
      pipWindow.addEventListener("pagehide", () => setPipActive(false));

      const styles = Array.from(document.styleSheets)
        .filter((s) => s.href)
        .map((s) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = s.href!;
          return link;
        });

      const fonts = Array.from(document.head.querySelectorAll("link[rel='preconnect'], link[href*='fonts']"))
        .map((node) => node.cloneNode(true));

      fonts.forEach((f) => pipWindow.document.head.appendChild(f));
      styles.forEach((s) => pipWindow.document.head.appendChild(s));

      const theme = document.documentElement.classList.contains("dark") ? "dark" : "light";
      pipWindow.document.documentElement.classList.add(theme);

      const html = `
        <div class="h-screen w-screen bg-[var(--window-bg)] text-[var(--text)] flex flex-col items-center justify-center p-6 text-center select-none">
           <div id="pip-wait" class="flex flex-col items-center gap-3 opacity-60">
             <div class="w-6 h-6 border-2 border-[var(--text-dim)] border-t-transparent rounded-full animate-spin"></div>
             <p class="text-xs font-mono text-[var(--text-dim)] uppercase tracking-wider">Esperando audio...</p>
           </div>
           <p id="pip-orig" class="text-sm text-[var(--text-dim)] font-medium mb-3 italic w-full line-clamp-2 leading-relaxed"></p>
           <p id="pip-tran" class="text-[28px] font-bold text-[var(--text)] tracking-tight w-full line-clamp-3 leading-snug drop-shadow-md"></p>
        </div>
      `;
      pipWindow.document.body.innerHTML = html;
      setPipActive(true);
    } catch (err: any) {
      console.error("Error al abrir PiP:", err);
      toast(
        "No se pudo iniciar la ventana flotante en tu navegador. Verifica que estés en una pestaña activa y que los permisos de ventanas flotantes estén habilitados.",
        { icon: "ℹ️", duration: 5000 }
      );
    }
  }, []);

  const togglePip = useCallback(() => {
    if (pipActive) closePip();
    else openPip();
  }, [pipActive, closePip, openPip]);

  return { pipActive, updatePip, togglePip, closePip };
}
